import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function CategorySection() {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<typeof categories[0] | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat);
    setIsOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section id="categories" className="w-full bg-background-50">
      <div className="px-6 md:px-10 py-14 md:py-20">
        <div className="text-center mb-10">
          <span className="inline-block px-4 py-1.5 rounded-full border border-foreground-200 text-foreground-600 text-xs font-medium tracking-wide uppercase mb-5">
            Catégories de Produits
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-900 mb-3">
            Explorez Nos <span className="text-accent-600">Catégories</span>
          </h2>
          <p className="text-foreground-600 text-sm max-w-xl mx-auto">
            Parcourez toutes les catégories du hipobuy spreadsheet et trouvez exactement ce que vous cherchez sur le site principal.
          </p>
        </div>

        <div className="flex flex-col items-center gap-8">
          <div className="relative w-full max-w-md" ref={dropdownRef}>
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="w-full flex items-center justify-between px-5 py-3.5 bg-background-100 border border-background-300 rounded-lg text-foreground-800 text-sm font-medium cursor-pointer hover:border-accent-300 transition-colors"
            >
              <span>{selectedCategory ? selectedCategory.nameFr : '— Sélectionnez une catégorie —'}</span>
              <i className={`${isOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-foreground-500 transition-transform`}></i>
            </button>

            {isOpen && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-background-50 border border-background-200 rounded-lg shadow-lg z-40 max-h-72 overflow-y-auto">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => handleSelect(cat)}
                    className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer border-b border-background-100 last:border-b-0"
                  >
                    {cat.nameFr}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="w-full">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 md:gap-4">
              {categories.map((cat) => (
                <a
                  key={cat.id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="group relative flex flex-col items-center rounded-xl bg-background-100 border border-background-200 overflow-hidden cursor-pointer hover:border-accent-300 hover:shadow-md transition-all"
                >
                  <div className="w-full h-28 md:h-32 overflow-hidden">
                    <img
                      src={cat.imageUrl}
                      alt={cat.nameFr}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                    />
                  </div>
                  <div className="w-full px-3 py-3 text-center">
                    <span className="text-foreground-800 text-xs md:text-sm font-semibold whitespace-nowrap group-hover:text-accent-600 transition-colors">
                      {cat.nameFr}
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}